import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LifeBuoy, X, MessageCircle, Mail, PenTool, Calculator, ArrowLeft } from "lucide-react";
import { LINKS } from "@/lib/links";

/** WhatsApp do botão Ajuda — (19) 3504-6333 */
const HELP_WA = "551935046333";
const helpWaLink = (text) =>
  `https://wa.me/${HELP_WA}?text=${encodeURIComponent(text)}`;

const CARDS = [
  {
    id: "orcamento",
    icon: Calculator,
    color: "bg-blue-100 text-blue-700",
    title: "Orçamento online",
    lines: ["A partir de 1 unidade."],
    href: LINKS.home,
    hrefLabel: "www.printlivros.com.br",
  },
  {
    id: "editorial",
    icon: PenTool,
    color: "bg-amber-100 text-amber-800",
    title: "Serviços editoriais",
    lines: ["Diagramação, revisão, capa e mais."],
    href: "https://www.printlivros.com.br/editorial",
    hrefLabel: "www.printlivros.com.br/editorial",
  },
  {
    id: "email",
    icon: Mail,
    color: "bg-slate-200 text-slate-700",
    title: "Dúvidas e informações",
    lines: ["contato@printlivros.com.br", "Resposta em até 3 a 4 dias úteis."],
    href: "mailto:contato@printlivros.com.br",
    hrefLabel: null,
  },
  {
    id: "whatsapp",
    icon: MessageCircle,
    color: "bg-green-100 text-green-700",
    title: "Falar pelo WhatsApp",
    lines: ["Responda em até 10 minutos para manter o atendimento ativo."],
    href: null,
    hrefLabel: null,
  },
];

const inputCls =
  "block w-full mt-1.5 bg-white border-2 border-neutral-200 rounded-xl px-3.5 py-3 text-sm font-semibold text-neutral-900 focus:border-blue-600 focus:outline-none transition-colors";

export default function HelpFAB() {
  const [open, setOpen] = useState(false);
  const [waForm, setWaForm] = useState(false);
  const [nome, setNome] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [error, setError] = useState("");

  const closeAll = () => {
    setOpen(false);
    setWaForm(false);
    setNome("");
    setMensagem("");
    setError("");
  };

  const openWaForm = () => {
    setWaForm(true);
    setError("");
  };

  const submitWa = () => {
    const n = nome.trim();
    const m = mensagem.trim();
    if (!n || !m) {
      setError("Preencha o nome e o que deseja falar.");
      return;
    }
    const text = `*Print Livros — dúvida pelo site*\n\n*Nome:* ${n}\n*Mensagem:* ${m}`;
    window.open(helpWaLink(text), "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed right-4 bottom-5 lg:right-6 lg:bottom-6 z-[96]" data-testid="help-widget">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.96 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
            className="mb-3 w-[384px] max-w-[calc(100vw-2rem)] max-h-[78vh] bg-white rounded-2xl shadow-2xl border border-neutral-200 overflow-hidden flex flex-col"
            data-testid="help-panel"
          >
            <div className="bg-gradient-to-r from-neutral-900 to-blue-800 text-white px-6 py-4 flex items-center justify-between shrink-0">
              <div>
                <p className="font-bold text-lg tracking-tight">{waForm ? "Falar no WhatsApp" : "Central de ajuda"}</p>
                <p className="text-xs text-white/70 mt-0.5">
                  {waForm ? "Preencha para iniciar a conversa" : "Escolha como prefere falar conosco"}
                </p>
              </div>
              <button
                data-testid="help-close"
                onClick={closeAll}
                aria-label="Fechar ajuda"
                className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              <AnimatePresence mode="wait">
                {!waForm ? (
                  <motion.div
                    key="cards"
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-2"
                  >
                    {CARDS.map((c) => (
                      <div
                        key={c.id}
                        data-testid={`help-card-${c.id}`}
                        className="rounded-xl border-2 border-neutral-100 bg-[#FDFCF9] px-3.5 py-3.5"
                      >
                        <div className="flex items-start gap-3">
                          <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${c.color}`}>
                            <c.icon size={18} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="font-bold text-[15px] text-neutral-900 leading-snug">{c.title}</p>
                            {c.lines.map((line) => (
                              <p key={line} className="text-sm text-neutral-600 mt-0.5 leading-snug">
                                {c.id === "email" && line.includes("@") ? (
                                  <a href={`mailto:${line}`} className="font-semibold text-blue-700 hover:underline">
                                    {line}
                                  </a>
                                ) : (
                                  line
                                )}
                              </p>
                            ))}
                            {c.href && c.hrefLabel && (
                              <a
                                href={c.href}
                                target={c.href.startsWith("http") ? "_blank" : undefined}
                                rel={c.href.startsWith("http") ? "noreferrer" : undefined}
                                className="mt-1.5 inline-block text-sm font-bold text-blue-600 hover:text-blue-800 break-all"
                              >
                                {c.hrefLabel}
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                ) : (
                  <motion.div
                    key="wa-form"
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 12 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-4 px-1 py-1"
                    data-testid="help-wa-form"
                  >
                    <button
                      type="button"
                      data-testid="help-wa-back"
                      onClick={() => { setWaForm(false); setError(""); }}
                      className="flex items-center gap-1.5 text-[13px] font-bold text-blue-700 bg-blue-50 border-2 border-blue-200 rounded-full px-4 py-2 hover:bg-blue-100 transition-colors"
                    >
                      <ArrowLeft size={14} /> Voltar
                    </button>
                    <label className="block text-sm font-bold text-neutral-600">
                      Nome
                      <input
                        data-testid="help-wa-nome"
                        type="text"
                        value={nome}
                        onChange={(e) => { setNome(e.target.value); setError(""); }}
                        placeholder="Seu nome"
                        className={inputCls}
                        autoComplete="name"
                      />
                    </label>
                    <label className="block text-sm font-bold text-neutral-600">
                      O que deseja falar?
                      <textarea
                        data-testid="help-wa-mensagem"
                        value={mensagem}
                        onChange={(e) => { setMensagem(e.target.value); setError(""); }}
                        placeholder="Escreva sua dúvida ou pedido"
                        rows={4}
                        className={`${inputCls} resize-none`}
                      />
                    </label>
                    {error && (
                      <p data-testid="help-wa-error" className="text-sm font-bold text-red-600">
                        {error}
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="p-4 border-t border-neutral-100 bg-[#F8F7F4] shrink-0">
              {!waForm ? (
                <button
                  type="button"
                  data-testid="help-whatsapp"
                  onClick={openWaForm}
                  className="w-full bg-[#25D366] text-white font-bold text-sm py-3.5 rounded-xl flex items-center justify-center gap-2 hover:brightness-95 transition-all"
                >
                  <MessageCircle size={16} /> Falar no WhatsApp agora
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="help-wa-submit"
                  onClick={submitWa}
                  className="w-full bg-[#25D366] text-white font-bold text-sm py-3.5 rounded-xl flex items-center justify-center gap-2 hover:brightness-95 transition-all"
                >
                  <MessageCircle size={16} /> Abrir WhatsApp
                </button>
              )}
              <p className="text-center text-[11px] text-neutral-400 mt-2">(19) 3504-6333</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <button
        data-testid="help-fab"
        onClick={() => (open ? closeAll() : setOpen(true))}
        className="fab-pulse h-14 pl-4 pr-5 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 text-white flex items-center gap-2 shadow-xl hover:scale-105 transition-transform ml-auto"
        aria-label="Abrir central de ajuda"
      >
        {open ? <X size={20} /> : <LifeBuoy size={20} />}
        <span className="font-bold">{open ? "Fechar" : "Ajuda"}</span>
      </button>
    </div>
  );
}
